import type {WorkspaceMobileNavigationConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import {resolveConfigFormHref} from "~/workspaceConfig/configHref";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    OttopilotAdditionalButtonKind,
    OttopilotAdditionalButtonFormFieldPresentation,
    OttopilotAdditionalButtonFormFieldRole,
    OttopilotOperator,
    OttopilotRunTimeValueKind,
    type OttopilotAdditionalButtonVisibilityRule,
    type OttopilotAdditionalButtonsConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {
    OttopilotDispositionEmitWhen,
    OttopilotDispositionOptionRuleActionKind,
    OttopilotDispositionOptionRuleCountMode,
    OttopilotDispositionPluginKind,
    OttopilotSuggestedJourneyStatusRuleKind,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {
    OttopilotScheduleValueEncoding,
    OttopilotTrigger,
    OttopilotTriggerDataDriver,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    type WorkspaceCapability,
    WorkspaceCapabilityStatus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {TABLE_ACTIONS_COLUMN_ID} from "~/components/ottopilotDataViewer/columnConfiguration";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import type {TableRowActionsConfig, TableRowActionsMenuItem, TableRowActionsToolbarItem} from "~/components/table/tableRowActions.types";
import type {EventContainerConstraint} from "~/domainModels/workspaceModules/eventContainersModule";
import {isEventContainerBlockedByGlobalConstraints} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {getEventContainerColumnValues, type EventWithEventContainer, type EventContainerWithEvents} from "~/utilities/apis/api";
import {dispositionToLevelFormat, formatOttopilotDisplayDateTime, getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";
import {indianPhoneNumberValidationRegex} from "@vesta/global-common-typescript/utilities/validationPatterns";
import {
    JourneyCustomerStatusEnum,
    journeyCustomerStatuses,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Customers";
import {
    AppointmentStatusEnum,
    appointmentStatuses,
    CallStatusEnum,
    callStatuses,
    DULUX__APPOINTMENT__EDIT__FORM__ID,
    DULUX__APPOINTMENT__LOG__FORM__ID,
    DULUX__APPOINTMENT__SCHEDULE__FORM__ID,
    DULUX__CALL__EDIT__FORM__ID,
    DULUX__CALL__LOG__FORM__ID,
    DULUX__CALL__SCHEDULE__FORM__ID,
    DULUX__EVENT__APPOINTMENT__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CREATED_BY_ROLE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_REMARKS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_REMARKS__COLUMN_ID,
    DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CITY__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CONSENT_FOR_MARKETING_EMAIL__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CONSENT_FOR_PERSONAL_DATA__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__PINCODE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__STATE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    DULUX__EVENT__LEADS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DuluxEventTypes,
    EnquiryStatusEnum,
    enquiryStatuses,
    EnquiryTypeEnum,
    enquiryTypes,
    logCallButtonId,
    OnboardingStatusEnum,
    onboardingStatuses,
    SalesStatusEnum,
    salesStatuses,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Events";
import {
    DULUX__EVENT_CONTAINER__APPOINTMENT_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CALL_LOG_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CITY__COLUMN_ID,
    DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__EMAIL__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ONBOARDING_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PLATFORM__COLUMN_ID,
    DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SOURCE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__STATE__COLUMN_ID,
    DuluxEventContainerTypes,
    EventContainerDispositionEnum,
    eventContainerDispositions,
    LeadEnquiryTypeEnum,
    leadEnquiryTypes,
    DuluxLeadStatusEnum,
    leadStatuses,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/EventContainers";
import {
    DULUX__OU__COUNTRY__NAME__COLUMN_ID,
    DULUX__OU__STATE__NAME__COLUMN_ID,
    DULUX__OU__ZONE__NAME__COLUMN_ID,
    DuluxOus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/OrganizationalUnits";
import {
    DULUX__USER_MDO__STATES__COLUMN_ID,
    DULUX__USER_RMM__REGIONS__COLUMN_ID,
    DuluxRoles,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Users";
import {DuluxModuleNames} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Workspace";

export {
    eventContainerDispositions,
    leadStatuses,
    salesStatuses,
    journeyCustomerStatuses,
    leadEnquiryTypes,
    enquiryStatuses,
    enquiryTypes,
    onboardingStatuses,
    callStatuses,
    appointmentStatuses,
};
export {
    DuluxOus,
    DuluxRoles,
    DuluxEventTypes,
    DuluxModuleNames,
    DULUX__OU__COUNTRY__NAME__COLUMN_ID,
    DULUX__OU__ZONE__NAME__COLUMN_ID,
    DULUX__OU__STATE__NAME__COLUMN_ID,
    DULUX__USER_RMM__REGIONS__COLUMN_ID,
    DULUX__USER_MDO__STATES__COLUMN_ID,
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__EMAIL__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PLATFORM__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SOURCE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__STATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CITY__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CALL_LOG_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__APPOINTMENT_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ONBOARDING_COUNT__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__PINCODE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__STATE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CITY__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CONSENT_FOR_PERSONAL_DATA__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__CONSENT_FOR_MARKETING_EMAIL__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    DULUX__EVENT__LEADS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_REMARKS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CREATED_BY_ROLE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_REMARKS__COLUMN_ID,
    EventContainerDispositionEnum,
    DuluxLeadStatusEnum,
    SalesStatusEnum,
    JourneyCustomerStatusEnum,
    LeadEnquiryTypeEnum,
    EnquiryStatusEnum,
    EnquiryTypeEnum,
    OnboardingStatusEnum,
    CallStatusEnum,
    AppointmentStatusEnum,
    logCallButtonId,
    DULUX__CALL__SCHEDULE__FORM__ID,
    DULUX__CALL__LOG__FORM__ID,
    DULUX__CALL__EDIT__FORM__ID,
    DULUX__APPOINTMENT__SCHEDULE__FORM__ID,
    DULUX__APPOINTMENT__LOG__FORM__ID,
    DULUX__APPOINTMENT__EDIT__FORM__ID,
};

export function isDuluxWorkspace(workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>): boolean {
    return workspaceCapabilities.some(
        (capability) => capability.type === WorkspaceCapabilityType.duluxSpecificBehaviour && capability.status === WorkspaceCapabilityStatus.enabled,
    );
}

export function isDuluxBlueStoreLeadEventContainer(eventContainer: {templateId: Uuid}): boolean {
    return eventContainer.templateId === (DuluxEventContainerTypes.BlueStoreLeads as Uuid);
}

export function isDuluxBlueStoreCallOrWalkinEvent(event: {templateId: Uuid}): boolean {
    return (
        event.templateId === (DuluxEventTypes.BlueStoreCalls as Uuid) ||
        event.templateId === (DuluxEventTypes.BlueStoreWalkins as Uuid)
    );
}

/**
 * Blocks journey/event mutations when lead status is not Open Lead.
 * Inactive = Aid Onboarded, Churned, or Archived.
 */
export const DULUX_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS: Array<EventContainerConstraint> = [
    {
        type: "Status",
        columnType: "OttopilotEventContainer",
        refColumnId: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        isAdditionalColumn: true,
        disallowedStatuses: [
            leadStatuses[DuluxLeadStatusEnum.AID_ONBOARDED].id,
            leadStatuses[DuluxLeadStatusEnum.CHURNED].id,
            leadStatuses[DuluxLeadStatusEnum.ARCHIVED].id,
        ],
    },
];

export function isDuluxJourneyLeadStatusBlockingEdit(journey: EventContainerWithEvents | Omit<EventContainerWithEvents, "events">): boolean {
    return isEventContainerBlockedByGlobalConstraints(journey, DULUX_JOURNEY_NON_EDITABLE_LEAD_STATUS_GLOBAL_CONSTRAINTS);
}

/** Restricts JourneyPicker lead options to Open Lead (active) journeys on Dulux create forms. */
export const DULUX_ACTIVE_JOURNEY_PICKER_FILTER_LINE_ITEMS: Array<FilterConfigurationLineItem> = [
    {
        columnId: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        operation: FilterOperation.equals,
        value: leadStatuses[DuluxLeadStatusEnum.OPEN_LEAD].id,
    },
];

export function isDuluxPhoneNumberPrefilled(phoneNumber: string | null | undefined): boolean {
    return (phoneNumber ?? "").trim() !== "";
}

function normalizeDuluxPhoneDigits(phoneNumber: string): string {
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91") === true) {
        return digits.slice(2);
    }
    if (digits.length === 11 && digits.startsWith("0") === true) {
        return digits.slice(1);
    }
    return digits;
}

/** Returns an error message when phone is empty/invalid; otherwise null. */
export function getDuluxCreateFormPhoneValidationError(phoneNumber: string | null | undefined): string | null {
    const trimmed = (phoneNumber ?? "").trim();
    if (trimmed === "") {
        return "Phone number is required.";
    }
    const normalized = normalizeDuluxPhoneDigits(trimmed);
    if (indianPhoneNumberValidationRegex.test(normalized) !== true) {
        return "Enter a valid 10-digit phone number.";
    }
    return null;
}

/** Returns an error message when a required date is missing or invalid; otherwise null. */
export function getDuluxCreateFormRequiredDateValidationError(value: unknown, fieldLabel: string): string | null {
    if (value === null || value === undefined) {
        return `Please select a ${fieldLabel}`;
    }
    if (value instanceof Date && Number.isNaN(value.getTime()) === true) {
        return `Please enter a valid ${fieldLabel}`;
    }
    return null;
}

/** Kanban card grid overrides for Dulux list modules (leads, calls, etc.) on narrow viewports. */
export const DULUX_MODULE_KANBAN_LAYOUT = {
    mobileCardsGridTemplateColumns: "1fr",
} as const;

function duluxPrimitiveToString(raw: unknown): string {
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean" || typeof raw === "bigint") {
        return raw.toString();
    }
    return "";
}

export function getDuluxListScheduleRouteForEventTemplate(workspaceId: Uuid, eventTemplateId: Uuid): string {
    const formId =
        eventTemplateId === (DuluxEventTypes.Appointments as Uuid)
            ? DULUX__APPOINTMENT__SCHEDULE__FORM__ID
            : DULUX__CALL__SCHEDULE__FORM__ID;
    return resolveConfigFormHref({workspaceId, formId}) ?? "";
}

const DULUX_EVENT_CREATE_FORM_URL_PREFIXES = ["dulux/call/", "dulux/appointment/"] as const;

export function getDuluxListEventCreateItems(
    workspaceMobileNavigationConfiguration: WorkspaceMobileNavigationConfiguration | null,
    eventTemplateId?: Uuid,
): readonly WorkspaceMobileBottomCreateItem[] {
    const items = workspaceMobileNavigationConfiguration?.mobileBottomCreateSheet ?? [];
    const prefixes =
        eventTemplateId === (DuluxEventTypes.Calls as Uuid)
            ? (["dulux/call/"] as const)
            : eventTemplateId === (DuluxEventTypes.Appointments as Uuid)
              ? (["dulux/appointment/"] as const)
              : DULUX_EVENT_CREATE_FORM_URL_PREFIXES;
    return items.filter((item) => prefixes.some((prefix) => item.formUrl.startsWith(prefix)));
}

export type DuluxListModule = "journeysLeads" | "events";

export type DuluxListModuleConfig = {
    enabled: boolean;
    showFooter: boolean;
    scheduleRoute: string;
    includeEventCreateDrawer: boolean;
};

export const DULUX_LIST_MODULE_CONFIG: Readonly<Record<DuluxListModule, DuluxListModuleConfig>> = {
    journeysLeads: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
    events: {
        enabled: true,
        showFooter: true,
        scheduleRoute: "",
        includeEventCreateDrawer: true,
    },
};

export function getDuluxListModuleConfig(module: DuluxListModule): DuluxListModuleConfig {
    return DULUX_LIST_MODULE_CONFIG[module];
}

// Organizational Unit Template IDs
// User Template IDs
// Event Template IDs
// Module IDs
// Organizational Unit Column IDs

// User Column IDs

// Array of MDO user IDs that report to this RMM

// Journey Column IDs

// export const DULUX__EVENT_CONTAINER__CREATED_AT__COLUMN_ID = "66a61b24-fa6f-41d7-a296-ef7d7cc22677" as Uuid;
// export const DULUX__EVENT_CONTAINER__REGION__COLUMN_ID = "c0eebc99-9c0b-4ef8-bb6d-6bb9bd380a13" as Uuid;
// export const DULUX__EVENT_CONTAINER__CUSTOMER_STATUS__COLUMN_ID = "bc0d6ff2-66a5-4489-91e5-63e280062e7d" as Uuid;

/** Journey columns used in list table configs but omitted from module `additionalColumns`. */
export const DULUX_JOURNEY_FALLBACK_TABLE_COLUMN_DISPLAY_NAMES: Readonly<Partial<Record<Uuid, string>>> = {
    [DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID]: "Company Name",
    [DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID]: "Pincode",
    [DULUX__EVENT_CONTAINER__STATE__COLUMN_ID]: "State",
    [DULUX__EVENT_CONTAINER__CITY__COLUMN_ID]: "City",
};

export function resolveDuluxJourneyFallbackTableColumnDisplayName(columnId: string): string | undefined {
    return DULUX_JOURNEY_FALLBACK_TABLE_COLUMN_DISPLAY_NAMES[columnId as Uuid];
}

const DULUX_NON_EXPORTABLE_TABLE_COLUMN_IDS = new Set(["id", "actionButton", TABLE_ACTIONS_COLUMN_ID, "__actions__"]);

export function duluxColumnIsExportable(col: {id: string; valueGetter?: unknown}): boolean {
    if (DULUX_NON_EXPORTABLE_TABLE_COLUMN_IDS.has(col.id)) {
        return false;
    }
    return typeof col.valueGetter === "function";
}

export const DULUX_JOURNEYS_DEFAULT_SEARCH_COLUMN_IDS: ReadonlyArray<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
];

export function resolveDuluxJourneysSearchColumns(configuredSearchColumns: ReadonlyArray<string>): Array<string> {
    if (configuredSearchColumns.length > 0) {
        return [...configuredSearchColumns];
    }
    return [...DULUX_JOURNEYS_DEFAULT_SEARCH_COLUMN_IDS];
}

export const DULUX_DEFAULT_SEARCH_PLACEHOLDER = "Search by name, contact number...";

export function getDuluxJourneysSearchPlaceholder(): string {
    return DULUX_DEFAULT_SEARCH_PLACEHOLDER;
}

export const DULUX_EVENTS_DEFAULT_SEARCH_COLUMN_IDS: ReadonlyArray<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
];

const DULUX_LIST_EVENT_TEMPLATE_IDS: ReadonlySet<string> = new Set([
    String(DuluxEventTypes.Calls),
    String(DuluxEventTypes.Appointments),
    String(DuluxEventTypes.Enquiry),
    String(DuluxEventTypes.Onboarding),
]);

export function isDuluxEventTemplateId(eventTemplateId: Uuid): boolean {
    return DULUX_LIST_EVENT_TEMPLATE_IDS.has(String(eventTemplateId)) === true;
}

export function resolveDuluxEventSearchColumns(configuredSearchColumns: ReadonlyArray<string>, eventTemplateId: Uuid): Array<string> {
    if (configuredSearchColumns.length > 0) {
        return [...configuredSearchColumns];
    }
    if (DULUX_LIST_EVENT_TEMPLATE_IDS.has(String(eventTemplateId)) !== true) {
        return [];
    }
    return [...DULUX_EVENTS_DEFAULT_SEARCH_COLUMN_IDS];
}

export function getDuluxEventSearchPlaceholder(eventTemplateId: Uuid): string | undefined {
    if (DULUX_LIST_EVENT_TEMPLATE_IDS.has(String(eventTemplateId)) !== true) {
        return undefined;
    }
    return DULUX_DEFAULT_SEARCH_PLACEHOLDER;
}

const DULUX_JOURNEY_LEADS_SYSTEM_FILTER_FIELDS = new Set(["id", "assignedTo", "createdBy", "createdAt", "updatedBy", "status"]);

const DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DULUX_JOURNEY_LEADS_SYSTEM_FIELD_BY_DISPLAY_NAME: Readonly<Record<string, string>> = {
    id: "id",
    "assigned to": "assignedTo",
    "assigned agent": "assignedTo",
    "created by": "createdBy",
    "created on": "createdAt",
    "modified by": "updatedBy",
    status: "status",
};

export type DuluxJourneyLeadsColumnFilterDeps = {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    /** Event-container (journey) template id for column-values. */
    eventContainerTemplateId: Uuid;
    tableViewColumns?: ReadonlyArray<{id: string; field: string; displayName?: string}>;
};

export type DuluxJourneyLeadsTableColumnDeps = DuluxJourneyLeadsColumnFilterDeps & {
    visibleColumnIds: ReadonlyArray<string>;
    additionalColumns: ReadonlyArray<{id: Uuid; displayName: string}>;
};

export function buildDuluxJourneyLeadsTableColumns(
    baseColumns: Array<OttopilotColumn<EventContainerWithEvents>>,
    deps: DuluxJourneyLeadsTableColumnDeps,
): Array<OttopilotColumn<EventContainerWithEvents>> {
    const columnById = new Map(baseColumns.map((column) => [column.id, column]));
    const additionalColumnById = new Map(deps.additionalColumns.map((column) => [column.id, column]));

    const explicitColumns = deps.visibleColumnIds.flatMap((columnId): Array<OttopilotColumn<EventContainerWithEvents>> => {
        const existing = columnById.get(columnId);
        if (existing !== undefined) {
            return [existing];
        }

        const fallbackDisplayName = resolveDuluxJourneyFallbackTableColumnDisplayName(columnId);
        const additionalColumn = additionalColumnById.get(columnId as Uuid);
        const displayName = fallbackDisplayName ?? additionalColumn?.displayName;
        if (displayName === undefined) {
            return [];
        }

        return [
            {
                id: columnId,
                type: OttopilotColumnType.string,
                displayName,
                valueGetter: (journey: EventContainerWithEvents) => {
                    const raw = journey.additionalColumnValues[columnId as Uuid];
                    return duluxPrimitiveToString(raw);
                },
                sortingCapability: true,
                filteringCapability_contains: true,
            } satisfies OttopilotColumn<EventContainerWithEvents>,
        ];
    });

    const resolvedColumns = explicitColumns.length > 0 ? explicitColumns : baseColumns;
    return applyDuluxJourneyLeadsColumnFiltering(resolvedColumns, deps);
}

function resolveDuluxJourneyLeadsSystemField(
    column: OttopilotColumn<EventContainerWithEvents>,
    tableViewColumns: DuluxJourneyLeadsColumnFilterDeps["tableViewColumns"],
): string | null {
    const fieldFromConfig = tableViewColumns?.find((item) => item.id === column.id)?.field;
    if (fieldFromConfig !== undefined && DULUX_JOURNEY_LEADS_SYSTEM_FILTER_FIELDS.has(fieldFromConfig) === true) {
        return fieldFromConfig;
    }
    if (DULUX_JOURNEY_LEADS_SYSTEM_FILTER_FIELDS.has(column.id) === true) {
        return column.id;
    }
    const fromDisplayName = DULUX_JOURNEY_LEADS_SYSTEM_FIELD_BY_DISPLAY_NAME[column.displayName.trim().toLowerCase()];
    return fromDisplayName ?? null;
}

export function applyDuluxJourneyLeadsColumnFiltering(
    columns: Array<OttopilotColumn<EventContainerWithEvents>>,
    deps: DuluxJourneyLeadsColumnFilterDeps,
): Array<OttopilotColumn<EventContainerWithEvents>> {
    const journeyColumnValuesQueryFn = async (systemField: string) => {
        const result = await getEventContainerColumnValues({
            workspaceId: deps.workspaceId,
            columnId: systemField,
            intellsysBaseUrl: deps.intellsysBaseUrl,
            accessToken: deps.accessToken,
            templateId: deps.eventContainerTemplateId,
        });
        if (result.success !== true) {
            throw result.error;
        }
        return result.data;
    };

    return columns.map((column): OttopilotColumn<EventContainerWithEvents> => {
        if (String(column.id) === String(DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID)) {
            return {
                id: column.id,
                displayName: column.displayName,
                type: OttopilotColumnType.string,
                valueGetter: (journey: EventContainerWithEvents) =>
                    duluxPrimitiveToString(journey.additionalColumnValues[DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID]),
                valueFormatter: (value: string) => resolveDuluxLeadStatusDisplayName(value),
                sortingCapability: column.sortingCapability ?? true,
                filteringCapability_contains: false,
                filteringCapability_in: {
                    valuesQueryFn: async () => getDuluxLeadStatusFilterValueIds(),
                },
                pinned: column.pinned,
            };
        }

        const systemField = resolveDuluxJourneyLeadsSystemField(column, deps.tableViewColumns);
        if (systemField === null) {
            return column;
        }

        if (systemField === "id") {
            return {
                id: column.id,
                displayName: column.displayName.trim() === "" ? "ID" : column.displayName,
                type: OttopilotColumnType.uuid,
                valueGetter: (journey: EventContainerWithEvents) => journey.id,
                sortingCapability: true,
                filteringCapability_contains: false,
                filteringCapability_in: undefined,
                pinned: column.pinned,
            } satisfies OttopilotColumn<EventContainerWithEvents>;
        }

        if (systemField === "createdAt") {
            return {
                id: column.id,
                displayName: column.displayName,
                type: OttopilotColumnType.iso8601DateTime,
                valueGetter: (journey: EventContainerWithEvents): Iso8601DateTime => journey.createdAt,
                valueFormatter: (value) => formatOttopilotDisplayDateTime(value),
                sortingCapability: true,
                filteringCapability_contains: true,
                pinned: column.pinned,
            } satisfies OttopilotColumn<EventContainerWithEvents>;
        }

        if (systemField === "createdBy" || systemField === "updatedBy") {
            const getRawUserId =
                systemField === "createdBy"
                    ? (journey: EventContainerWithEvents) => journey.createdBy
                    : (journey: EventContainerWithEvents) => journey.updatedBy;
            return {
                ...column,
                type: OttopilotColumnType.string,
                valueGetter: (journey: EventContainerWithEvents): string => {
                    const raw = getRawUserId(journey);
                    const trimmed = String(raw).trim();
                    return trimmed === "" ? "" : trimmed;
                },
                valueFormatter: (value: string): string => {
                    const trimmed = value.trim();
                    if (trimmed === "") {
                        return "";
                    }
                    if (DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(trimmed) !== true) {
                        return trimmed;
                    }
                    const name = getUserDisplayName(trimmed as Uuid, deps.users, deps.userTemplates, deps.platformUserDetailsByUserId);
                    return DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(name) === true ? "-" : name;
                },
                sortingCapability: true,
                filteringCapability_contains: false,
                filteringCapability_in: {
                    valuesQueryFn: async () => journeyColumnValuesQueryFn(systemField),
                },
            };
        }

        if (systemField === "assignedTo") {
            return {
                ...column,
                type: OttopilotColumnType.string,
                valueGetter: (journey: EventContainerWithEvents): string => {
                    const raw = journey.additionalColumnValues[DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID];
                    if (typeof raw !== "string") {
                        return "";
                    }
                    const trimmed = raw.trim();
                    return trimmed === "" ? "" : trimmed;
                },
                valueFormatter: (value: string): string => {
                    const trimmed = value.trim();
                    if (trimmed === "") {
                        return "";
                    }
                    if (DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(trimmed) !== true) {
                        return trimmed;
                    }
                    const name = getUserDisplayName(trimmed as Uuid, deps.users, deps.userTemplates, deps.platformUserDetailsByUserId);
                    return DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(name) === true ? "-" : name;
                },
                sortingCapability: true,
                filteringCapability_contains: false,
                filteringCapability_in: {
                    valuesQueryFn: async () => journeyColumnValuesQueryFn(String(DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID)),
                },
            };
        }

        if (systemField === "status") {
            return {
                ...column,
                type: OttopilotColumnType.string,
                valueGetter: (journey: EventContainerWithEvents) => journey.status,
                valueFormatter: (value: string) => eventContainerStatusToDisplayName(value as EventContainerWithEvents["status"]),
                sortingCapability: true,
                filteringCapability_contains: true,
                filteringCapability_in: {
                    valuesQueryFn: async () => journeyColumnValuesQueryFn("status"),
                },
            };
        }

        return column;
    });
}

const DULUX_EVENT_SYSTEM_FILTER_FIELDS = new Set(["updatedBy", "updatedAt", "createdAt"]);

const DULUX_EVENT_SYSTEM_FIELD_BY_DISPLAY_NAME: Readonly<Record<string, string>> = {
    "updated by": "updatedBy",
    "updated at": "updatedAt",
    "modified by": "updatedBy",
    "modified on": "updatedAt",
    "created at": "createdAt",
    "created on": "createdAt",
};

export type DuluxEventTableColumnFilterDeps = {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    updatedByValuesQueryFn: () => Promise<Array<string>>;
};

function resolveDuluxEventSystemField(column: OttopilotColumn<EventWithEventContainer>): string | null {
    if (DULUX_EVENT_SYSTEM_FILTER_FIELDS.has(column.id) === true) {
        return column.id;
    }
    const fromDisplayName = DULUX_EVENT_SYSTEM_FIELD_BY_DISPLAY_NAME[column.displayName.trim().toLowerCase()];
    return fromDisplayName ?? null;
}

const DULUX_EVENT_CANCELLATION_COLUMN_LOOKUP = new Map<string, readonly [Uuid, string]>([
    ...[DULUX__EVENT__CALLS__CANCELLED_DATE__COLUMN_ID, DULUX__EVENT__CALLS__CANCELLED_BY__COLUMN_ID, DULUX__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID].map(
        (id) => [String(id), [DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID, CallStatusEnum.CANCELLED]] as const,
    ),
    ...[
        DULUX__EVENT__APPOINTMENT__CANCELLED_DATE__COLUMN_ID,
        DULUX__EVENT__APPOINTMENT__CANCELLED_BY__COLUMN_ID,
        DULUX__EVENT__APPOINTMENT__CANCELLATION_REMARKS__COLUMN_ID,
    ].map((id) => [String(id), [DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID, AppointmentStatusEnum.CANCELLED]] as const),
]);

function applyDuluxEventCancellationColumnValue(
    column: OttopilotColumn<EventWithEventContainer>,
    statusColumnId: Uuid,
    requiredStatus: string,
): OttopilotColumn<EventWithEventContainer> {
    const columnId = column.id as Uuid;
    const emptyValue = column.type === OttopilotColumnType.iso8601DateTime ? ("" as Iso8601DateTime) : "";
    const originalFormatter = column.valueFormatter;

    return {
        ...column,
        valueGetter: (event: EventWithEventContainer) => {
            const status = event.additionalColumnValues[statusColumnId];
            if (typeof status !== "string" || status.trim() !== requiredStatus) {
                return emptyValue;
            }
            const raw = event.additionalColumnValues[columnId];
            if (raw === null || raw === undefined || String(raw).trim() === "") {
                return emptyValue;
            }
            return column.type === OttopilotColumnType.iso8601DateTime ? (raw as Iso8601DateTime) : String(raw);
        },
        valueFormatter: (value: never) => {
            if (value === null || value === undefined || String(value).trim() === "") {
                return "-";
            }
            if (originalFormatter !== undefined) {
                return (originalFormatter as (formattedValue: never) => string)(value);
            }
            return column.type === OttopilotColumnType.iso8601DateTime ? formatOttopilotDisplayDateTime(value as Iso8601DateTime) : String(value);
        },
    } as OttopilotColumn<EventWithEventContainer>;
}

export function formatDuluxEventUserReferenceDisplay(
    userId: unknown,
    deps: {
        users: Array<User>;
        userTemplates: Array<UserTemplate>;
        platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    },
): string {
    if (userId === null || userId === undefined) {
        return "-";
    }
    if (typeof userId !== "string") {
        return "-";
    }
    const trimmed = userId.trim();
    if (trimmed === "") {
        return "-";
    }
    if (DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(trimmed) !== true) {
        return trimmed;
    }
    const name = getUserDisplayName(trimmed as Uuid, deps.users, deps.userTemplates, deps.platformUserDetailsByUserId);
    return DULUX_JOURNEY_USER_REFERENCE_UUID_PATTERN.test(name) === true ? "-" : name;
}

export function applyDuluxEventTableColumnFiltering(
    columns: Array<OttopilotColumn<EventWithEventContainer>>,
    deps: DuluxEventTableColumnFilterDeps,
): Array<OttopilotColumn<EventWithEventContainer>> {
    return columns.map((column): OttopilotColumn<EventWithEventContainer> => {
        const cancellationLookup = DULUX_EVENT_CANCELLATION_COLUMN_LOOKUP.get(String(column.id));
        if (cancellationLookup !== undefined) {
            return applyDuluxEventCancellationColumnValue(column, cancellationLookup[0], cancellationLookup[1]);
        }

        const systemField = resolveDuluxEventSystemField(column);
        if (systemField === null) {
            return column;
        }

        if (systemField === "updatedAt" || systemField === "createdAt") {
            const isUpdatedAt = systemField === "updatedAt";
            return {
                id: column.id,
                type: OttopilotColumnType.iso8601DateTime,
                displayName: column.displayName.trim() === "" ? (isUpdatedAt ? "Updated at" : "Created at") : column.displayName,
                valueGetter: (event: EventWithEventContainer): Iso8601DateTime => (isUpdatedAt ? event.updatedAt : event.createdAt),
                valueFormatter: (value) => formatOttopilotDisplayDateTime(value),
                sortingCapability: column.sortingCapability ?? true,
                filteringCapability_contains: true,
                filteringCapability_in: undefined,
                pinned: column.pinned,
            };
        }

        if (systemField === "updatedBy") {
            return {
                id: column.id,
                displayName: column.displayName,
                type: OttopilotColumnType.string,
                valueGetter: (event: EventWithEventContainer): string => {
                    const trimmed = String(event.updatedBy).trim();
                    return trimmed === "" ? "" : trimmed;
                },
                valueFormatter: (value: string): string => {
                    const trimmed = value.trim();
                    if (trimmed === "") {
                        return "";
                    }
                    return formatDuluxEventUserReferenceDisplay(trimmed, deps);
                },
                sortingCapability: column.sortingCapability ?? true,
                filteringCapability_contains: false,
                filteringCapability_in: {
                    valuesQueryFn: deps.updatedByValuesQueryFn,
                },
                pinned: column.pinned,
            };
        }

        return column;
    });
}
// export const DULUX__EVENT_CONTAINER__ONBOARDING_STATUS__COLUMN_ID = "56196f1f-6679-464d-8884-872c506208ae" as Uuid;
// export const DULUX__EVENT_CONTAINER__ENQUIRY_TYPE__COLUMN_ID = "d335bdaa-f700-40ee-95dc-3fedbe814cb6" as Uuid;
// export const DULUX__EVENT_CONTAINER__EVENT_OWNER__COLUMN_ID = "daf82933-c902-4dde-b049-ad3824428fa0" as Uuid;

// Lead (Journey with dispositions) Column IDs

// export const DULUX__LEAD__FULL_NAME__COLUMN_ID = "ad5d5c60-83a1-4c46-b93c-b1481adf39f8" as Uuid;
// export const DULUX__LEAD__CONTACT_NUMBER__COLUMN_ID = "05e52892-e1b1-4365-8a26-a72e33533ee0" as Uuid;
// export const DULUX__LEAD__LEAD_STATUS__COLUMN_ID = "93719f7b-2f0f-4fe0-a4d8-8bcbc5f8f8e8" as Uuid;
// export const DULUX__LEAD__EMAIL__COLUMN_ID = "98047bb1-b536-4f70-9303-67744929ce51" as Uuid;
// export const DULUX__LEAD__SALES_STATUS__COLUMN_ID = "13293aa0-f28c-4c55-b1a3-3287938541f6" as Uuid;
// export const DULUX__LEAD__CREATED_AT__COLUMN_ID = "df950840-3014-465f-8a78-4e103eb023c3" as Uuid;
// export const DULUX__LEAD__CREATED_BY_NAME__COLUMN_ID = "f13a6da1-bd32-48dd-9a28-9926390c4470" as Uuid;
// export const DULUX__LEAD__CREATED_BY_ROLE__COLUMN_ID = "5287155e-19e0-4698-9dbb-68d8f7d8a8ea" as Uuid;
// export const DULUX__LEAD__ASSIGNED_TO__COLUMN_ID = "15a5f2fa-3ea2-4c68-86c9-1ab7ffe5f839" as Uuid;
// export const DULUX__LEAD__ASSIGNED_ON__COLUMN_ID = "07a1a452-dcb3-481c-8646-d9766f042b4b" as Uuid;
// export const DULUX__LEAD__ENQUIRY_TYPE__COLUMN_ID = "c520c383-5674-453b-8d50-c83e0af389e4" as Uuid;
// export const DULUX__LEAD__LAST_EVENT_TIMESTAMP__COLUMN_ID = "49ded3c8-db3c-407e-a51f-e17d9fdb328f" as Uuid;
// export const DULUX__LEAD__LAST_EVENT__COLUMN_ID = "2286a98f-9235-4aeb-9dc6-aab9c5b1071e" as Uuid;

// Event Column IDs - Enquiry

// export const DULUX__EVENT__ENQUIRY__FULL_NAME__COLUMN_ID = "6c9f6052-4141-4160-8c1a-05b157e959ac" as Uuid;
// export const DULUX__EVENT__ENQUIRY__REGION__COLUMN_ID = "d0eebc99-9c0b-4ef8-bb6d-6bb9bd380a14" as Uuid;
// export const DULUX__EVENT__ENQUIRY__CONTACT_NUMBER__COLUMN_ID = "286e0d9a-d496-45c1-8fd2-7c4e2d31e5aa" as Uuid;
// export const DULUX__EVENT__ENQUIRY__EMAIL__COLUMN_ID = "cf416fbb-34ce-421d-945b-32a0d9a25586" as Uuid;
// export const DULUX__EVENT__ENQUIRY__PRODUCT__COLUMN_ID = "f35807e3-94f9-453b-9dd1-fd0e8335116f" as Uuid;
// export const DULUX__EVENT__ENQUIRY__ADDRESS__COLUMN_ID = "50375524-211b-43d8-9bf8-b0b719c40cf4" as Uuid;
// export const DULUX__EVENT__ENQUIRY__CREATED_AT__COLUMN_ID = "fe1e3230-7dbc-437d-a7a4-692fb1e58871" as Uuid;
// export const DULUX__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID = "3adcb309-114d-4b8a-b31e-4648a94c6335" as Uuid;
// export const DULUX__EVENT__ENQUIRY__ASSIGNED_TO__COLUMN_ID = "1eb256cb-a2bf-455f-9257-85cb1ae092d5" as Uuid;
// export const DULUX__EVENT__ENQUIRY__ENQUIRY_TYPE__COLUMN_ID = "74ffe3ae-24c0-4b6a-a42b-91831806f2c9" as Uuid;

// Event Column IDs - Leads

// export const DULUX__EVENT__LEADS__FULL_NAME__COLUMN_ID = "9fdf7634-ab9a-42bd-b32a-1fe395bf291f" as Uuid;
// export const DULUX__EVENT__LEADS__CONTACT_NUMBER__COLUMN_ID = "e3a73323-c1f2-4ab8-acff-f0c5e3989b7f" as Uuid;
// export const DULUX__EVENT__LEADS__LEAD_STATUS__COLUMN_ID = "0a2930c2-0c51-46b1-afea-15cd5ac423bb" as Uuid;
// export const DULUX__EVENT__LEADS__EMAIL__COLUMN_ID = "10198ff6-24cc-49e5-91ac-793a99609dd0" as Uuid;
// export const DULUX__EVENT__LEADS__SALES_STATUS__COLUMN_ID = "4f1271ae-918e-4196-bc61-953e8e6dec52" as Uuid;
// export const DULUX__EVENT__LEADS__CREATED_AT__COLUMN_ID = "24a439ca-fd78-4814-8bf8-3877010c7c56" as Uuid;
// export const DULUX__EVENT__LEADS__CREATED_BY_NAME__COLUMN_ID = "0ef60f3a-aa25-4509-a906-1d3ac8744a6f" as Uuid;
// export const DULUX__EVENT__LEADS__CREATED_BY_ROLE__COLUMN_ID = "0883333a-1989-4c7b-9e88-a0a4ce36267e" as Uuid;
// export const DULUX__EVENT__LEADS__ASSIGNED_TO__COLUMN_ID = "31d30225-efb5-4d8b-abf9-132f4e1681fa" as Uuid;
// export const DULUX__EVENT__LEADS__ASSIGNED_ON__COLUMN_ID = "eaa11756-5484-45c5-8221-7a0f2a421575" as Uuid;
// export const DULUX__EVENT__LEADS__ENQUIRY_TYPE__COLUMN_ID = "d335bdaa-f700-40ee-95dc-3fedbe814cb6" as Uuid;
// export const DULUX__EVENT__LEADS__LAST_EVENT_TIMESTAMP__COLUMN_ID = "b8a3758d-7e09-480b-82ac-2330b96c97a8" as Uuid;
// export const DULUX__EVENT__LEADS__LAST_EVENT__COLUMN_ID = "795d142a-5ccd-47da-9cec-38dc00d6a7d9" as Uuid;
// export const DULUX__EVENT__LEADS__COMPANY_NAME__COLUMN_ID = "16f0a452-96c3-4516-9644-39375ef601bf" as Uuid;
// export const DULUX__EVENT__LEADS__PINCODE__COLUMN_ID = "4635ed40-570f-4dda-ae75-7073f368b4a1" as Uuid;
// export const DULUX__EVENT__LEADS__STATE__COLUMN_ID = "34a64be8-8ebc-4554-86b2-8eaef1ff857f" as Uuid;
// export const DULUX__EVENT__LEADS__CITY__COLUMN_ID = "d2b912b4-76f2-4d43-9be4-6c0b1752525d" as Uuid;
// export const DULUX__EVENT__LEADS__ONBOARDING_STATUS__COLUMN_ID = "56196f1f-6679-464d-8884-872c506208ae" as Uuid;
// export const DULUX__EVENT__LEADS__DISPOSITION__COLUMN_ID = "fcb61788-1713-4f15-a6d3-999dd7d2b3bf" as Uuid;

// Event Column IDs - Onboarding
// export const DULUX__EVENT__ONBOARDING__FULL_NAME__COLUMN_ID = "34257297-b2f7-42c9-a3b2-e4ebe695f492" as Uuid;
// export const DULUX__EVENT__ONBOARDING__REGION__COLUMN_ID = "e0eebc99-9c0b-4ef8-bb6d-6bb9bd380a15" as Uuid;
// export const DULUX__EVENT__ONBOARDING__COMPANY_NAME__COLUMN_ID = "4ed60c92-5059-49aa-a2fb-0a088fa3e4c8" as Uuid;
// export const DULUX__EVENT__ONBOARDING__CONTACT_NUMBER__COLUMN_ID = "d0e2f465-d2c0-4521-8495-648e1ec77b5c" as Uuid;
// export const DULUX__EVENT__ONBOARDING__PINCODE__COLUMN_ID = "de40d5e0-652c-4c2b-a9e9-4157e6f2fa33" as Uuid;
// export const DULUX__EVENT__ONBOARDING__STATE__COLUMN_ID = "b851e11e-3602-44c8-a56c-cce9dacc47af" as Uuid;
// export const DULUX__EVENT__ONBOARDING__CITY__COLUMN_ID = "a06b63a9-d61b-4923-9b11-df0b3840759f" as Uuid;
// export const DULUX__EVENT__ONBOARDING__EMAIL__COLUMN_ID = "1db26f5d-1d58-47a1-8e65-1512e70414d7" as Uuid;
// export const DULUX__EVENT__ONBOARDING__CONSENT_FOR_PERSONAL_DATA__COLUMN_ID = "cf33d46f-39f4-464b-a687-63ea5527a3c4" as Uuid;
// export const DULUX__EVENT__ONBOARDING__CONSENT_FOR_MARKETING_EMAIL__COLUMN_ID = "4eef17eb-3800-4798-ad74-9e5e6c381707" as Uuid;
// export const DULUX__EVENT__ONBOARDING__ASSIGNED_TO__COLUMN_ID = "25e52e59-2b97-443a-815e-914830c98c1f" as Uuid;
// export const DULUX__EVENT__ONBOARDING__ENQUIRY_TYPE__COLUMN_ID = "fe506bd3-3524-4cb9-bb5b-6a992818b80f" as Uuid;

// Event Column IDs - Calls

// export const DULUX__EVENT__CALLS__FULL_NAME__COLUMN_ID = "df327fac-5007-46a9-948e-06000452180c" as Uuid;
// export const DULUX__EVENT__CALLS__REGION__COLUMN_ID = "f0eebc99-9c0b-4ef8-bb6d-6bb9bd380a16" as Uuid;
// export const DULUX__EVENT__CALLS__COMPANY_NAME__COLUMN_ID = "6d2f52c9-84ab-4390-8fbb-423c09dc8002" as Uuid;
// export const DULUX__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID = "f4f0325c-b222-491a-9c65-86c12c642063" as Uuid;
// export const DULUX__EVENT__CALLS__ASSIGNED_TO__COLUMN_ID = "e9a69cfb-97e1-4c41-ae52-2bf8bb2c364a" as Uuid;
// export const DULUX__EVENT__CALLS__PINCODE__COLUMN_ID = "2fe1a9bb-2479-48ef-810f-4baaf22485ae" as Uuid;
// export const DULUX__EVENT__CALLS__STATE__COLUMN_ID = "858e1023-f8d6-4cd0-9281-e67fe5ae4fb8" as Uuid;
// export const DULUX__EVENT__CALLS__CITY__COLUMN_ID = "e108c283-1026-4f9b-b864-09da01e9eb6a" as Uuid;
// export const DULUX__EVENT__CALLS__EMAIL__COLUMN_ID = "5defcbc0-0267-4f55-bfbf-58f766493455" as Uuid;
// export const DULUX__EVENT__CALLS__ENQUIRY_TYPE__COLUMN_ID = "bf4e3f42-7a51-439c-bab6-1eae43332eb7" as Uuid;

// Event Column IDs - Appointments

/** Event owner fields store a user id but may be typed as uuid/text in the event template. */
export const DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS: ReadonlyArray<Uuid> = [
    DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT__LEADS__EVENT_OWNER__COLUMN_ID,
];

/** Status column on the event row for each Dulux event template (for overview / panel display). */
export function getDuluxEventStatusColumnIdForEventTemplate(eventTemplateId: Uuid): Uuid | undefined {
    if (eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)) {
        return DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)) {
        return DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
        return DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
        return DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Leads as Uuid)) {
        return DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID;
    }
    return undefined;
}

/** Human-readable export filename for Dulux event list CSV/PNG downloads (e.g. `calls-export.csv`). */
export function resolveDuluxEventExportFilename(eventTemplateId: Uuid, extension: "csv" | "png"): string | undefined {
    const eventTypeEntry = Object.entries(DuluxEventTypes).find(([, id]) => String(id) === eventTemplateId);
    if (eventTypeEntry === undefined) {
        return undefined;
    }
    return `${eventTypeEntry[0].toLowerCase()}-export.${extension}`;
}

/** Event owner (user id) column for each Dulux event template. */
export function getDuluxEventOwnerColumnIdForEventTemplate(eventTemplateId: Uuid): Uuid | undefined {
    if (eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)) {
        return DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)) {
        return DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
        return DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
        return DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID;
    }
    if (eventTemplateId === (DuluxEventTypes.Leads as Uuid)) {
        return DULUX__EVENT__LEADS__EVENT_OWNER__COLUMN_ID;
    }
    return undefined;
}

/** Leads (`/journeys`) — explicit columns for table and mobile list. */
export const DULUX_JOURNEY_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    "disposition",
    DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PLATFORM__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SOURCE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CALL_LOG_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__APPOINTMENT_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ONBOARDING_COUNT__COLUMN_ID,
    "createdBy",
    "createdAt",
    "updatedBy",
    "updatedAt",
    "status",
];

/** Calls events list — explicit column order. */
export const DULUX_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__LOGGED_REMARKS__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    "createdAt",
];

/** Appointments events list — explicit column order. */
export const DULUX_APPOINTMENTS_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LAST_DISPOSITION__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__LOGGED_REMARKS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_BY__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLED_DATE__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__CANCELLATION_REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    "createdAt",
];

/** Enquiry events list — explicit column order. */
export const DULUX_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__STATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CITY__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    "createdAt",
];

type DuluxKanbanBoardCardView = OttopilotEventTemplate["configuration"]["eventPage"]["kanbanBoardCardView"];
type DuluxKanbanCardDetailConfig = DuluxKanbanBoardCardView["details"][number][number];

const DULUX_ENQUIRY_KANBAN_EXCLUDED_COLUMN_IDS: ReadonlySet<string> = new Set([
    String(DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__STATE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__CITY__COLUMN_ID),
    String(DULUX__EVENT__ENQUIRY__PINCODE__COLUMN_ID),
    String(DULUX__EVENT__ENQUIRY__STATE__COLUMN_ID),
    String(DULUX__EVENT__ENQUIRY__CITY__COLUMN_ID),
]);

const DULUX_ENQUIRY_KANBAN_EXCLUDED_DETAIL_LABELS: ReadonlySet<string> = new Set(["pincode", "state", "city"]);

const DULUX_CALLS_KANBAN_EXCLUDED_COLUMN_IDS: ReadonlySet<string> = new Set([String(DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID)]);

const DULUX_CALLS_KANBAN_EXCLUDED_DETAIL_LABELS: ReadonlySet<string> = new Set(["status"]);

function duluxKanbanDetailConfigIsExcluded(
    columnDetail: DuluxKanbanCardDetailConfig,
    excludedColumnIds: ReadonlySet<string>,
    excludedDetailLabels: ReadonlySet<string>,
): boolean {
    if (excludedDetailLabels.has(columnDetail.label.trim().toLowerCase()) === true) {
        return true;
    }
    const dataSources = columnDetail.type === "SingleColumn" ? [columnDetail.dataSource] : columnDetail.dataSource;
    return dataSources.some((dataSource) => excludedColumnIds.has(String(dataSource)));
}

function filterDuluxKanbanBoardCardView(
    kanbanBoardCardView: DuluxKanbanBoardCardView,
    excludedColumnIds: ReadonlySet<string>,
    excludedDetailLabels: ReadonlySet<string>,
): DuluxKanbanBoardCardView {
    return {
        ...kanbanBoardCardView,
        heading: kanbanBoardCardView.heading.filter((id) => excludedColumnIds.has(String(id)) === false),
        details: kanbanBoardCardView.details
            .map((row) => row.filter((col) => duluxKanbanDetailConfigIsExcluded(col, excludedColumnIds, excludedDetailLabels) === false))
            .filter((row) => row.length > 0),
    };
}

/** Dulux event kanban - template-specific fields hidden on cards (e.g. enquiry location, call status). */
export function resolveDuluxEventKanbanBoardCardView(eventTemplateId: Uuid, kanbanBoardCardView: DuluxKanbanBoardCardView): DuluxKanbanBoardCardView {
    if (eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)) {
        return filterDuluxKanbanBoardCardView(kanbanBoardCardView, DULUX_ENQUIRY_KANBAN_EXCLUDED_COLUMN_IDS, DULUX_ENQUIRY_KANBAN_EXCLUDED_DETAIL_LABELS);
    }
    if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
        return filterDuluxKanbanBoardCardView(kanbanBoardCardView, DULUX_CALLS_KANBAN_EXCLUDED_COLUMN_IDS, DULUX_CALLS_KANBAN_EXCLUDED_DETAIL_LABELS);
    }
    return kanbanBoardCardView;
}

/** Onboarding events list — explicit column order. */
export const DULUX_ONBOARDING_EVENT_TABLE_VISIBLE_COLUMN_IDS: Array<string> = [
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__COMPANY_NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__STATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CITY__COLUMN_ID,
    "updatedBy",
    "updatedAt",
    "createdAt",
];

const DULUX_NEW_LEAD_DISPOSITION = {
    id: "e8f4a2b1-6c3d-4e5f-9a0b-2d3e4f5a6b7c" as Uuid,
    displayName: "New Lead",
    flatDisposition: "new-lead",
};

const DULUX_AID_KIT_NEEDED_DISPOSITION_ID =
    eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_AID_KIT_NEEDED_NON_SERVICEABLE_AREA].id;

function listDuluxJourneyDispositionDefinitions(): Array<{
    id: Uuid;
    displayName: string;
    flatDisposition: string;
}> {
    return [
        DULUX_NEW_LEAD_DISPOSITION,
        ...Object.values(eventContainerDispositions).map((disposition) => {
            if (disposition.id !== DULUX_AID_KIT_NEEDED_DISPOSITION_ID) {
                return disposition;
            }
            return {
                ...disposition,
                displayName: "Connected | Valid | Interested | AID Kit needed",
                flatDisposition: "connected_valid_interested_aid-kit-needed",
            };
        }),
    ];
}

export const DULUX_NEW_LEAD_DISPOSITION_ID = DULUX_NEW_LEAD_DISPOSITION.id;

export const DULUX_JOURNEY_DISPOSITION_NEW_LEAD_L1_SLUG = DULUX_NEW_LEAD_DISPOSITION.flatDisposition;

export function getDuluxEventContainerDispositions(): Array<{id: Uuid; displayName: string}> {
    return listDuluxJourneyDispositionDefinitions()
        .filter((disposition) => disposition.id !== DULUX_NEW_LEAD_DISPOSITION_ID)
        .map((disposition) => ({
            id: disposition.id,
            displayName: dispositionToLevelFormat(disposition.flatDisposition),
        }));
}

export function getDuluxEventContainerDispositionsForModule(): Array<{id: Uuid; displayName: string}> {
    return listDuluxJourneyDispositionDefinitions().map((disposition) => ({
        id: disposition.id,
        displayName: disposition.flatDisposition,
    }));
}

const DULUX_LEGACY_CUSTOMER_STATUS_ID_TO_LEAD_DISPLAY_NAME: Partial<Record<string, string>> = {
    [JourneyCustomerStatusEnum.NEW]: leadStatuses[DuluxLeadStatusEnum.OPEN_LEAD].displayName,
    [JourneyCustomerStatusEnum.ACTIVE]: leadStatuses[DuluxLeadStatusEnum.AID_ONBOARDED].displayName,
    [JourneyCustomerStatusEnum.EXPIRED]: leadStatuses[DuluxLeadStatusEnum.CHURNED].displayName,
    [JourneyCustomerStatusEnum.REPEAT]: leadStatuses[DuluxLeadStatusEnum.CHURNED].displayName,
};

export function resolveDuluxLeadStatusDisplayName(raw: unknown): string {
    const idStr = duluxPrimitiveToString(raw).trim();
    if (idStr === "") {
        return leadStatuses[DuluxLeadStatusEnum.OPEN_LEAD].displayName;
    }
    const leadEntry = Object.values(leadStatuses).find((status) => String(status.id) === idStr);
    if (leadEntry !== undefined) {
        return leadEntry.displayName;
    }
    const legacyDisplayName = DULUX_LEGACY_CUSTOMER_STATUS_ID_TO_LEAD_DISPLAY_NAME[idStr];
    if (legacyDisplayName !== undefined) {
        return legacyDisplayName;
    }
    return idStr;
}

export function getDuluxLeadStatusFilterValueIds(): Array<string> {
    return Object.values(leadStatuses).map((status) => String(status.id));
}

export function getDuluxJourneyLeadsKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: uniqueUuid`6071cd85-a4a6-40af-bccd-171135c62a4d`,
            displayName: leadStatuses[DuluxLeadStatusEnum.OPEN_LEAD].displayName,
            background: token("color.background.accent.red.subtlest"),
            textColor: token("color.text.accent.red"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [leadStatuses[DuluxLeadStatusEnum.OPEN_LEAD].id],
        },
        {
            id: uniqueUuid`4f9e2a6c-d4c8-4b9e-8f7b-9d6a4c2f18b3`,
            displayName: leadStatuses[DuluxLeadStatusEnum.AID_ONBOARDED].displayName,
            background: token("color.background.accent.orange.subtlest"),
            textColor: token("color.text.accent.orange"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [leadStatuses[DuluxLeadStatusEnum.AID_ONBOARDED].id],
        },
        {
            id: uniqueUuid`bd09e95d-caff-44fb-84ba-6216b005e631`,
            displayName: leadStatuses[DuluxLeadStatusEnum.CHURNED].displayName,
            background: token("color.background.accent.green.subtlest"),
            textColor: token("color.text.accent.green"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [leadStatuses[DuluxLeadStatusEnum.CHURNED].id],
        },
    ];
}

// Journey Disposition Enum
// Lead Status Enum (for Leads event)
// Sales Status Enum
// Journey Customer Status Enum
// Lead Enquiry Type Enum (for Leads event)
// Enquiry Status Enum
// Enquiry Type Enum
// Onboarding Status Enum
// Call Status Enum
const duluxCallsScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: CallStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

const duluxAppointmentsScheduledVisibilityRule: OttopilotAdditionalButtonVisibilityRule = {
    conditions: [
        {
            attribute: DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
            operator: OttopilotOperator.equals,
            kind: OttopilotRunTimeValueKind.static,
            value: AppointmentStatusEnum.SCHEDULED as string,
        },
    ],
    logicalOperator: "and",
};

/** Cancel + Log for scheduled Dulux calls. Calls events page hides Log (row action covers it). */
export const DULUX__CALLS_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "57f77702-0513-4644-8218-7997edfc27fc" as Uuid,
        label: "Cancel call",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description: "Cancels a scheduled call.",
        rules: [duluxCallsScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: DULUX__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: DULUX__EVENT__CALLS__CANCELLED_DATE__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: DULUX__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: CallStatusEnum.CANCELLED,
                sortOrder: 3,
            },
        ],
    },
    {
        id: "b1a09f8e-2c3d-4e5f-8a9b-0c1d2e3f4a5b" as Uuid,
        label: "Log",
        buttonKind: OttopilotAdditionalButtonKind.log,
        rules: [duluxCallsScheduledVisibilityRule],
        formUrl: "dulux/call/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

/** Log action for Dulux Appointments when the appointment is scheduled. */
export const DULUX__APPOINTMENTS_EVENT__ADDITIONAL_BUTTONS: Array<OttopilotAdditionalButtonsConfig> = [
    {
        id: "047111bb-c45a-47d5-9e6d-a9ed13dedde6" as Uuid,
        label: "Cancel appointment",
        buttonKind: OttopilotAdditionalButtonKind.cancel,
        description: "Cancels a scheduled appointment.",
        rules: [duluxAppointmentsScheduledVisibilityRule],
        showModal: true,
        formColumnData: [
            {
                columnId: DULUX__EVENT__APPOINTMENT__CANCELLED_BY__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.user,
                sortOrder: 0,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__CANCELLED_DATE__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.timestamp,
                sortOrder: 1,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__CANCELLATION_REMARKS__COLUMN_ID,
                columnType: "OttopilotEvent",
                fieldRole: OttopilotAdditionalButtonFormFieldRole.cancelRemarks,
                isVisible: true,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                presentation: OttopilotAdditionalButtonFormFieldPresentation.multiLineText,
                sortOrder: 2,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                isVisible: false,
                isRequired: true,
                kind: OttopilotRunTimeValueKind.static,
                defaultValue: AppointmentStatusEnum.CANCELLED,
                sortOrder: 3,
            },
        ],
    },
    {
        id: "a09e8d7c-1b2a-4c3d-8e7f-6a5b4c3d2e1f" as Uuid,
        label: "Log",
        buttonKind: OttopilotAdditionalButtonKind.log,
        rules: [duluxAppointmentsScheduledVisibilityRule],
        formUrl: "dulux/appointment/log",
        formUrlQueryParams: ["eventContainerId", "eventId"],
    },
];

// Call form Ids

// Appointment form Ids

export const DULUX_DEFAULT_JOURNEY_TRIGGER_CONFIG: OttopilotTriggerConfig = [
    {
        type: OttopilotTrigger.dateTimeTrigger,
        eventTemplateId: DuluxEventTypes.Calls as Uuid,
        triggerDispositions: [
            eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL1].id,
            eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL2].id,
            eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL3].id,
        ],
        dataColumns: [
            {
                columnId: DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: CallStatusEnum.SCHEDULED,
            },
            {
                columnId: DULUX__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUserTemplateId,
            },
            {
                columnId: DULUX__EVENT__CALLS__EVENT_OWNER__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: null,
            },
            {
                columnId: DULUX__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.eventContainerDisposition,
            },
            {
                columnId: DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: DuluxEventTypes.Calls as Uuid,
            },
            {
                columnId: DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.currentTimestamp,
            },
        ],
        dateTimeColumnId: DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
        scheduleFieldLabel: "Schedule call",
        scheduleValueEncoding: OttopilotScheduleValueEncoding.duluxIst,
    },
    {
        type: OttopilotTrigger.dateTimeTrigger,
        eventTemplateId: DuluxEventTypes.Appointments as Uuid,
        triggerDispositions: [eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_APPOINTMENT_GIVEN].id],
        dataColumns: [
            {
                columnId: DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: AppointmentStatusEnum.SCHEDULED,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__CREATED_BY_ROLE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUserTemplateId,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__EVENT_OWNER__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__SCHEDULE_DATE__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: null,
            },
            {
                columnId: DULUX__EVENT__APPOINTMENT__LAST_DISPOSITION__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.eventContainerDisposition,
            },
        ],
        dateTimeColumnId: DULUX__EVENT__APPOINTMENT__SCHEDULE_DATE__COLUMN_ID,
        scheduleFieldLabel: "Schedule appointment",
        scheduleValueEncoding: OttopilotScheduleValueEncoding.duluxIst,
    },
    {
        type: OttopilotTrigger.noComponentTrigger,
        eventTemplateId: DuluxEventTypes.Onboarding as Uuid,
        triggerDispositions: [eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_AID_ONBOARDED].id],
        dataColumns: [
            {
                columnId: DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: OnboardingStatusEnum.ONBOARDED,
            },
            {
                columnId: DULUX__EVENT__ONBOARDING__EVENT_OWNER__COLUMN_ID,
                columnType: "OttopilotEvent",
                driver: OttopilotTriggerDataDriver.workspaceUser,
            },
            {
                columnId: DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.staticDefault,
                defaultValue: DuluxEventTypes.Onboarding as Uuid,
            },
            {
                columnId: DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
                columnType: "OttopilotEventContainer",
                driver: OttopilotTriggerDataDriver.currentTimestamp,
            },
        ],
    },
];

export const duluxDispositionConfig: OttopilotDispositionConfig = {
    emitWhen: OttopilotDispositionEmitWhen.pathMatchesKnownDisposition,
    plugins: [
        {
            kind: OttopilotDispositionPluginKind.dispositionHistory,
            lastN: 20,
        },
        {
            kind: OttopilotDispositionPluginKind.optionRules,
            rules: [
                {
                    id: "dulux-hide-new-lead-when-current-is-new-lead" as Uuid,
                    when: {
                        level: 0,
                        selectedPrefix: [],
                    },
                    targetSegment: "new-lead",
                    countDispositionId: DULUX_NEW_LEAD_DISPOSITION_ID,
                    countMode: OttopilotDispositionOptionRuleCountMode.totalInHistory,
                    action: {
                        kind: OttopilotDispositionOptionRuleActionKind.hideWhenCurrentDispositionIdEquals,
                    },
                },
                {
                    id: "dulux-hide-new-lead-after-first-change" as Uuid,
                    when: {
                        level: 0,
                        selectedPrefix: [],
                    },
                    targetSegment: "new-lead",
                    countDispositionId: DULUX_NEW_LEAD_DISPOSITION_ID,
                    countMode: OttopilotDispositionOptionRuleCountMode.totalInHistory,
                    action: {
                        kind: OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId,
                        allowedDispositionIds: [DULUX_NEW_LEAD_DISPOSITION_ID],
                    },
                },
            ],
        },
        {
            kind: OttopilotDispositionPluginKind.suggestedJourneyStatusRules,
            dispositionStatusColumnId: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
            rules: [
                {
                    kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet,
                    dispositionIds: [eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_AID_ONBOARDED].id],
                    suggested: DuluxLeadStatusEnum.AID_ONBOARDED as unknown as Uuid,
                },
                {
                    kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet,
                    dispositionIds: [
                        eventContainerDispositions[EventContainerDispositionEnum.NOT_CONNECTED_NC6].id,
                        eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_NON_SERVICEABLE_AREA].id,
                        eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_NOT_AN_AID].id,
                        eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL3].id,
                        eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_INVALID_DID_NOT_FILL].id,
                        eventContainerDispositions[EventContainerDispositionEnum.CONNECTED_INVALID_FILLED_BY_MISTAKE].id,
                        eventContainerDispositions[EventContainerDispositionEnum.WRONG_NUMBER].id,
                    ],
                    suggested: DuluxLeadStatusEnum.CHURNED as unknown as Uuid,
                },
            ],
        },
    ],
};

const DULUX_AID_ONBOARDED_ALLOWED_ROW_ACTIONS: ReadonlySet<TableRowActionsMenuItem | TableRowActionsToolbarItem> = new Set(["view", "profile"]);

/** Blocks journey edit and row actions when lead status is Aid Onboarded (view + profile only). */
export const DULUX_JOURNEY_AID_ONBOARDED_ROW_ACTION_GLOBAL_CONSTRAINTS: Array<EventContainerConstraint> = [
    {
        type: "Status",
        columnType: "OttopilotEventContainer",
        refColumnId: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        isAdditionalColumn: true,
        disallowedStatuses: [leadStatuses[DuluxLeadStatusEnum.AID_ONBOARDED].id],
    },
];

export function resolveDuluxJourneyGlobalConstraints(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
): Array<EventContainerConstraint> | undefined {
    if (isDuluxWorkspace(workspaceCapabilities) !== true) {
        return undefined;
    }
    return DULUX_JOURNEY_AID_ONBOARDED_ROW_ACTION_GLOBAL_CONSTRAINTS;
}

export function isDuluxAidOnboardedJourneyRowActionDisabled(
    action: TableRowActionsMenuItem | TableRowActionsToolbarItem,
    journey: Omit<EventContainerWithEvents, "events">,
): boolean {
    if (isEventContainerBlockedByGlobalConstraints(journey, DULUX_JOURNEY_AID_ONBOARDED_ROW_ACTION_GLOBAL_CONSTRAINTS) !== true) {
        return false;
    }
    if (action === "more") {
        return false;
    }
    return DULUX_AID_ONBOARDED_ALLOWED_ROW_ACTIONS.has(action) !== true;
}

function isDuluxAidOnboardedRestrictedJourney(journey: Omit<EventContainerWithEvents, "events">): boolean {
    return isEventContainerBlockedByGlobalConstraints(journey, DULUX_JOURNEY_AID_ONBOARDED_ROW_ACTION_GLOBAL_CONSTRAINTS) === true;
}

export function wrapDuluxAidOnboardedJourneyRowActions<TRow extends Omit<EventContainerWithEvents, "events">>(
    base: TableRowActionsConfig<TRow>,
): TableRowActionsConfig<TRow> {
    return {
        ...base,
        canEdit: (row) => {
            if (isDuluxAidOnboardedJourneyRowActionDisabled("edit", row) === true) {
                return false;
            }
            return base.canEdit?.(row) ?? true;
        },
        isToolbarItemDisabled: (item, journey) => {
            if (item === "more" && isDuluxAidOnboardedRestrictedJourney(journey) === true) {
                return false;
            }
            if (isDuluxAidOnboardedJourneyRowActionDisabled(item, journey) === true) {
                return true;
            }
            return base.isToolbarItemDisabled?.(item, journey) ?? false;
        },
        isMenuItemDisabled: (item, journey) => {
            if (isDuluxAidOnboardedJourneyRowActionDisabled(item, journey) === true) {
                return true;
            }
            return base.isMenuItemDisabled?.(item, journey) ?? false;
        },
    };
}
